// frontend/src/pages/BestTimePage.js
//
// Renders /best-time-to-post and its platform spokes from
// data/best-time-pages.json.
//
// The cluster is 2.55M searches at competition 1-2 — the largest soft
// opportunity in 51,364 keywords. Instagram alone is 2.1M of it (82%), which is
// why it gets the deepest page rather than being treated as one spoke among many.
//
// THE DAY-BY-DAY TABLE IS THE LONG-TAIL PLAY. 224 keywords worth 553,000
// searches are day-specific ("best time to post on instagram sunday"). Building
// a page per day per platform would be 56 near-identical pages — textbook
// doorway-page territory. One table covers them all with no cannibalisation.
//
// No year in any title: zero keywords in the cluster contain 2025 or 2026, so a
// year would force an annual rewrite for no search benefit.
import '../custom.css'

import React from 'react'
import { Container, Row, Col, Button, Accordion, Image } from 'react-bootstrap'
import { useLocation } from 'react-router-dom'
import { CheckCircleFill } from 'react-bootstrap-icons'

import Seo, { SITE_URL } from '../components/Seo'
import BestTimeTool from '../components/BestTimeTool'
import pages from '../data/best-time-pages.json'
import NotFound from './NotFound'

const HERO = {
  '':        ['/assets/images/articles/best-time-planning.webp',  'A weekly planner, laptop and coffee on a desk'],
  instagram: ['/assets/images/articles/best-time-instagram.webp', 'Someone holding coffee and a phone on a city street'],
  tiktok:    ['/assets/images/articles/best-time-tiktok.webp',    'A creator setting up a phone to film a video'],
}

const FAQS = platform => ([
  [`What is the best time to post on ${platform}?`,
   `There is no single answer that holds for every business — the times published everywhere are averages across industries that behave nothing alike. The day-by-day table on this page is a sensible starting point, and the free tool above builds a schedule around what your business actually does.`],
  ['Does posting time actually matter?',
   'It matters most on platforms where posts live briefly — Instagram feed posts, LinkedIn, X. It matters least on TikTok and Pinterest, where content keeps circulating for days or months. Consistency matters more than timing on every platform.'],
  ['How do I find the best time for my own audience?',
   'Your platform analytics show when your followers are actually online, by day and hour. That beats any published table. Post at those times for a fortnight, compare the results, and adjust.'],
  ['How often should I post?',
   'Three to five times a week that you can sustain beats seven for a fortnight followed by silence. Consistency is what the algorithms reward and what audiences notice.'],
  ['Is the schedule tool free?',
   'Yes — three schedules per session, no account needed. Create a free Greatly Social account to build as many as you like and schedule them automatically.'],
])

export default function BestTimePage () {
  const { pathname } = useLocation()
  const slug = pathname.replace(/^\/+best-time-to-post\/?/, '').replace(/\/+$/, '')
  const p = pages.find(x => x.slug === slug)
  if (!p) return <NotFound />

  const url = `/best-time-to-post${p.slug ? `/${p.slug}` : ''}`
  const faqs = FAQS(p.platform)
  const [heroSrc, heroAlt] = HERO[p.slug] || HERO['']

  return (
    <>
      <Seo
        title={p.seoTitle}
        description={p.seoDescription}
        path={url}
        image={heroSrc}
        jsonLd={[{
          '@context': 'https://schema.org', '@type': 'FAQPage',
          mainEntity: faqs.map(([q, a]) => ({
            '@type': 'Question', name: q,
            acceptedAnswer: { '@type': 'Answer', text: a },
          })),
        }]}
      />

      {/* ───────── HERO ───────── */}
      <section className='full-bleed py-5 bg-primary-soft'>
        <Container fluid='lg'>
          <Row className='align-items-center g-4'>
            <Col lg={7}>
              <h1 className='display-5 fw-bold mb-3'>{p.h1}</h1>
              <p className='lead mb-0'>{p.sub}</p>
            </Col>
            <Col lg={5}>
              <Image src={heroSrc} alt={heroAlt} fluid rounded loading='eager'
                     width={1200} height={800} className='shadow-sm' />
            </Col>
          </Row>
        </Container>
      </section>

      {/* ───────── TOOL — high, because it is the differentiator ───────── */}
      <section className='py-5'>
        <Container fluid='lg' style={{ maxWidth: 900 }}>
          <BestTimeTool platform={p.slug || 'instagram'} />
        </Container>
      </section>

      {/* ───────── THE HONEST ANSWER (snippet target) ───────── */}
      <section className='py-5 bg-light'>
        <Container style={{ maxWidth: 760 }}>
          {/* eslint-disable-next-line react/no-danger */}
          <p className='fs-5' dangerouslySetInnerHTML={{ __html: p.intro }} />
          <p className='text-muted fs-5 mb-0'>{p.intro2}</p>
        </Container>
      </section>

      {/* ───────── DAY-BY-DAY / PLATFORM TABLE ───────── */}
      <section className='py-5'>
        <Container fluid='lg' style={{ maxWidth: 880 }}>
          <h2 className='fw-bold mb-4'>
            {p.slug ? `Best time to post on ${p.platform}, day by day` : 'Best times by platform'}
          </h2>
          <div className='table-responsive mb-3'>
            <table className='table table-bordered align-middle'>
              <thead className='table-light'>
                <tr>{p.table_head.map(h => <th key={h}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {p.table.map((row, i) => (
                  <tr key={i}>
                    {row.map((cell, j) => (
                      <td key={j} className={j === 0 ? 'fw-semibold' : j === 1 ? 'text-primary fw-semibold' : 'text-muted'}>
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className='text-muted mb-0'>{p.outro}</p>
        </Container>
      </section>

      {/* ───────── PLATFORM DEPTH ───────── */}
      {p.sections && (
        <section className='py-5 bg-light'>
          <Container style={{ maxWidth: 760 }}>
            {p.sections.map(([heading, body]) => (
              <div className='mb-4' key={heading}>
                <h2 className='fw-bold h4 mb-2'>{heading}</h2>
                <p className='text-muted mb-0'>{body}</p>
              </div>
            ))}
          </Container>
        </section>
      )}

      {/* ───────── OTHER PLATFORMS ───────── */}
      <section className='py-5'>
        <Container fluid='lg' style={{ maxWidth: 880 }}>
          <h2 className='fw-bold h4 mb-3'>Best times on other platforms</h2>
          <Row className='g-3'>
            {pages.filter(x => x.slug !== p.slug).map(x => (
              <Col md={4} key={x.slug || 'pillar'}>
                <a href={`/best-time-to-post${x.slug ? `/${x.slug}` : ''}`}
                   className='d-block p-3 rounded-3 text-decoration-none h-100'
                   style={{ border: '1px solid #e5e7eb', background: '#fff' }}>
                  <div className='fw-semibold'>{x.platform}</div>
                  <div className='text-muted small'>{x.slug ? `Day by day` : 'Every platform compared'}</div>
                </a>
              </Col>
            ))}
            <Col md={4}>
              <a href='/calendar-view' className='d-block p-3 rounded-3 text-decoration-none h-100'
                 style={{ border: '1px solid #e5e7eb', background: '#fff' }}>
                <div className='fw-semibold'>Content Calendar</div>
                <div className='text-muted small'>Plan the whole month visually</div>
              </a>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ───────── WHAT IS GREATLY SOCIAL (standing rule 4) ───────── */}
      <section className='py-5'>
        <Container fluid='lg' style={{ maxWidth: 940 }}>
          <Row className='align-items-center g-4'>
            <Col md={6}>
              <h2 className='fw-bold mb-3'>What is Greatly Social?</h2>
              <p className='text-muted'>
                Greatly Social is a social media scheduler and content calendar. At Greatly
                Social you can write a post once, give it a different caption per network, and
                schedule it to all eight — Instagram, TikTok, Facebook, LinkedIn, YouTube,
                Pinterest, Threads and X — from a single calendar.
              </p>
              <p className='text-muted'>
                Which is the point of a posting schedule: the times above only help if
                something actually publishes at them. Plans start at $9/month and the trial
                needs no card.
              </p>
              <Button variant='primary' href='/register' className='me-2'>
                Start using Greatly Social free
              </Button>
              <Button variant='outline-primary' href='/plans'>See plans</Button>
            </Col>
            <Col md={6}>
              {/* A real capture of the product, not stock — see standing rule 2. */}
              <Image src='/assets/logo/calender.webp' alt='The Greatly Social content calendar'
                     fluid rounded className='shadow-sm' loading='lazy' />
            </Col>
          </Row>
        </Container>
      </section>

      {/* ───────── PRODUCT ───────── */}
      <section className='py-5 bg-light'>
        <Container fluid='lg' style={{ maxWidth: 880 }}>
          <h2 className='fw-bold mb-3'>Knowing the time is the easy part</h2>
          <p className='text-muted fs-5'>
            The hard part is being free at 7pm on a Wednesday. At Greatly Social you can
            schedule those slots once and let them publish while you get on with something
            else — to {p.platform === 'Social Media' ? 'all eight networks' : `${p.platform} and seven other networks`},
            from one calendar.
          </p>
          <Row className='g-3 mb-4'>
            {[['Schedule once, publish weekly', 'Set your slots and let them run.'],
              ['Eight networks, one post', 'A separate caption per network on the same post.'],
              ['A month in one upload', 'Queue a whole month from a spreadsheet.'],
              ['See the gaps', 'Empty days are obvious on a calendar, invisible in a list.']].map(([t, b]) => (
              <Col md={6} key={t}>
                <div className='d-flex align-items-start gap-2'>
                  <CheckCircleFill className='text-success mt-1 flex-shrink-0' />
                  <div><div className='fw-semibold'>{t}</div><div className='text-muted small'>{b}</div></div>
                </div>
              </Col>
            ))}
          </Row>
          <Button variant='primary' size='lg' href='/register' className='me-2'>
            Start using Greatly Social free
          </Button>
          <Button variant='outline-primary' size='lg' href='/plans'>See plans from $9/month</Button>
        </Container>
      </section>

      {/* ───────── FAQ ───────── */}
      <section className='py-5'>
        <Container style={{ maxWidth: 760 }}>
          <h2 className='fw-bold mb-4 text-center'>Common questions</h2>
          <Accordion flush defaultActiveKey='0'>
            {faqs.map(([q, a], i) => (
              <Accordion.Item eventKey={String(i)} key={i}>
                <Accordion.Header>{q}</Accordion.Header>
                <Accordion.Body className='text-muted'>{a}</Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </Container>
      </section>

      {/* ───────── CTA ───────── */}
      <section className='full-bleed py-5 text-center bg-primary text-white'>
        <div className='mx-auto px-4' style={{ maxWidth: 720 }}>
          <h2 className='fw-bold mb-3'>At Greatly Social you can post at the right time without being there</h2>
          <p className='lead mb-4'>
            Schedule your slots once and let them publish every week. Free for 14 days, no card,
            plans from $9/month.
          </p>
          <Button variant='light' size='lg' href='/register' className='me-2 mb-2'>
            Start using Greatly Social free
          </Button>
          <Button variant='outline-light' size='lg' href='/calendar-view' className='mb-2'>
            See the calendar
          </Button>
        </div>
      </section>
    </>
  )
}
